import { useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { ListChecks, RefreshCw, Settings2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ErrorState, LoadingState, PageHeader } from '@/components/composites/states'
import { ToneBadge } from '@/components/composites/status-badge'
import { usePermission } from '@/features/permissions/usePermission'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import { deadlineTitle, formatDuration, lateness, MILESTONE_LABEL, MILESTONE_PENDING, SEVERITY_LABEL, useDeskDashboard, useRunDesk, type DeskDashboard, type Milestone, type Severity } from './api'
import { SeverityBadge, useNow } from './parts'
import { InsightsSection } from './Insights'

const DAY = 24 * 3_600_000
type RangeKey = 'today' | '7d' | '30d' | 'mtd'
const RANGES: Array<{ id: RangeKey; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: 'Last 7 days' },
  { id: 'mtd', label: 'Month to date' },
  { id: '30d', label: 'Last 30 days' },
]
const startOfDay = () => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}
function rangeFor(key: RangeKey): { from: string; to: string } {
  const now = Date.now()
  const today = startOfDay()
  const from = key === 'today' ? today : key === '7d' ? today - 6 * DAY : key === '30d' ? today - 29 * DAY : new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime()
  // Rounded to the minute so the query key stays stable between renders.
  return { from: new Date(from).toISOString(), to: new Date(Math.ceil(now / 60_000) * 60_000).toISOString() }
}

const pct = (v: number | null) => (v === null ? '—' : `${v}%`)
const dur = (v: number | null) => (v === null ? '—' : formatDuration(v))
const FLAG_DOT = { red: 'bg-danger', amber: 'bg-warning', green: 'bg-success' } as const
const SEV_ORDER: Array<Severity | 'untriaged'> = ['P1', 'P2', 'P3', 'P4', 'REQ', 'untriaged']
const SEV_BAR: Record<string, string> = { P1: 'bg-danger', P2: 'bg-warning', P3: 'bg-info', P4: 'bg-text-muted', REQ: 'bg-brand', untriaged: 'bg-brand-accent' }

function Tile({ label, value, sub, tone }: { label: string; value: ReactNode; sub?: ReactNode; tone?: 'danger' | 'warning' }) {
  return (
    <div className={cn('rounded-lg border bg-card p-3 shadow-e1', tone === 'danger' ? 'border-danger/40' : tone === 'warning' ? 'border-warning/40' : 'border-border')}>
      <p className="text-tiny font-medium uppercase tracking-wide text-text-muted">{label}</p>
      <p className={cn('num mt-1 text-2xl font-semibold', tone === 'danger' ? 'text-danger' : tone === 'warning' ? 'text-warning' : 'text-foreground')}>{value}</p>
      {sub ? <p className="mt-0.5 text-tiny text-text-muted">{sub}</p> : null}
    </div>
  )
}

function Panel({ title, children, action, className }: { title: string; children: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <section className={cn('min-w-0 rounded-xl border border-border bg-card p-4 shadow-sm', className)}>
      <header className="mb-3 flex items-center gap-2">
        <h2 className="text-sm font-semibold">{title}</h2>
        {action ? <div className="ml-auto">{action}</div> : null}
      </header>
      {children}
    </section>
  )
}

function Th({ children, right }: { children: ReactNode; right?: boolean }) {
  return <th className={cn('px-2 py-1.5 text-tiny font-semibold uppercase tracking-wide text-text-muted', right ? 'text-right' : 'text-left')}>{children}</th>
}
function Td({ children, right, className }: { children: ReactNode; right?: boolean; className?: string }) {
  return <td className={cn('px-2 py-1.5 text-sm', right ? 'num text-right' : '', className)}>{children}</td>
}

function Aging({ d }: { d: DeskDashboard }) {
  const max = Math.max(1, ...d.aging.map((a) => a.total))
  return (
    <div className="grid gap-2">
      {d.aging.map((a) => (
        <div key={a.bucket} className="grid grid-cols-[6rem_1fr_2rem] items-center gap-2 text-sm">
          <span className="text-xs text-text-secondary">{a.bucket}</span>
          <div className="flex h-4 overflow-hidden rounded bg-muted" style={{ width: `${Math.max(4, (a.total / max) * 100)}%` }}>
            {SEV_ORDER.map((s) => {
              const n = Number(a[s] ?? 0)
              return n ? <span key={s} className={SEV_BAR[s]} style={{ width: `${(n / Math.max(1, a.total)) * 100}%` }} title={`${s === 'untriaged' ? 'Untriaged' : SEVERITY_LABEL[s]}: ${n}`} /> : null
            })}
          </div>
          <span className="num text-right text-xs">{a.total}</span>
        </div>
      ))}
      <div className="flex flex-wrap gap-3 pt-1 text-tiny text-text-muted">
        {SEV_ORDER.map((s) => (
          <span key={s} className="inline-flex items-center gap-1"><span className={cn('size-2 rounded-sm', SEV_BAR[s])} />{s === 'untriaged' ? 'Untriaged' : s}</span>
        ))}
      </div>
    </div>
  )
}

function Trend({ d }: { d: DeskDashboard }) {
  const max = Math.max(1, ...d.trend.flatMap((t) => [t.opened, t.closed]))
  return (
    <div>
      <div className="flex h-36 items-end gap-1 overflow-x-auto">
        {d.trend.map((t) => (
          <div key={t.day} className="flex min-w-6 flex-1 flex-col items-center gap-1" title={`${t.day}: opened ${t.opened}, closed ${t.closed}, backlog ${t.backlog}, SLA met ${pct(t.slaMetPct)}`}>
            <div className="flex h-28 w-full items-end justify-center gap-0.5">
              <span className="w-1/2 rounded-t bg-brand" style={{ height: `${(t.opened / max) * 100}%` }} />
              <span className="w-1/2 rounded-t bg-success" style={{ height: `${(t.closed / max) * 100}%` }} />
            </div>
            <span className="text-micro text-text-muted">{t.day.slice(8)}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-tiny text-text-muted">
        <span className="inline-flex items-center gap-1"><span className="size-2 rounded-sm bg-brand" />Opened</span>
        <span className="inline-flex items-center gap-1"><span className="size-2 rounded-sm bg-success" />Closed</span>
        <span>Backlog now {d.trend.at(-1)?.backlog ?? 0}</span>
      </div>
    </div>
  )
}

export function SupportDashboardPage() {
  const { workspaceSlug = '' } = useParams()
  const navigate = useNavigate()
  const [rangeKey, setRangeKey] = useState<RangeKey>('7d')
  const [range, setRange] = useState(() => rangeFor('7d'))
  const now = useNow()
  const pick = (key: RangeKey) => {
    setRangeKey(key)
    setRange(rangeFor(key))
  }
  const query = useDeskDashboard(range.from, range.to)
  const { has } = usePermission()
  const run = useRunDesk()
  const d = query.data
  const open = (id: string) => navigate(paths.supportDeskCase(workspaceSlug, id))

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        kicker="Support"
        title="Support dashboard"
        description="Live health across priorities, teams and clients. Every time is measured on the SLA clock from the customer's first message."
        actions={
          <>
            <Button asChild variant="outline" size="sm"><Link to={paths.supportDesk(workspaceSlug)}><ListChecks className="size-4" /> Queue</Link></Button>
            {has('support.configure') ? (
              <>
                <Button variant="outline" size="sm" disabled={run.isPending} onClick={() => run.mutate()}><RefreshCw className={cn('size-4', run.isPending && 'animate-spin')} /> Sync now</Button>
                <Button asChild variant="outline" size="sm"><Link to={paths.settingsSupportDesk(workspaceSlug)}><Settings2 className="size-4" /> Settings</Link></Button>
              </>
            ) : null}
          </>
        }
      />

      <nav className="mb-4 flex w-fit flex-wrap gap-1 rounded-md bg-muted p-1" aria-label="Period">
        {RANGES.map((r) => (
          <button key={r.id} type="button" onClick={() => pick(r.id)} className={cn('rounded px-3 py-1 text-sm', rangeKey === r.id ? 'bg-card font-medium shadow-sm' : 'text-text-secondary hover:bg-card/60')}>
            {r.label}
          </button>
        ))}
      </nav>

      {query.isPending ? (
        <LoadingState label="Working out the numbers…" />
      ) : query.error || !d ? (
        <ErrorState error={query.error} onRetry={query.refetch} />
      ) : (
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5 xl:grid-cols-9">
            <Tile label="Open" value={d.kpis.open} sub="right now" />
            <Tile label="Untriaged" value={d.kpis.untriaged} sub={d.kpis.oldestUntriagedMs ? `oldest ${formatDuration(d.kpis.oldestUntriagedMs)}` : 'none waiting'} tone={d.kpis.untriaged ? 'warning' : undefined} />
            <Tile label="At risk" value={d.kpis.atRisk} sub="under 1 hour" tone={d.kpis.atRisk ? 'warning' : undefined} />
            <Tile label="Breached" value={d.kpis.breachedOpen} sub="open now" tone={d.kpis.breachedOpen ? 'danger' : undefined} />
            <Tile label="SLA met" value={pct(d.kpis.slaMetPct)} sub="milestones in period" tone={d.kpis.slaMetPct !== null && d.kpis.slaMetPct < 95 ? 'danger' : undefined} />
            <Tile label="First response" value={dur(d.kpis.responseMedianMs)} sub={`median · P90 ${dur(d.kpis.responseP90Ms)}`} />
            <Tile label="Restore time" value={dur(d.kpis.restoreMedianMs)} sub={`median · P90 ${dur(d.kpis.restoreP90Ms)}`} />
            <Tile label="Updates overdue" value={d.kpis.updatesOverdue} sub="Cadence by priority" tone={d.kpis.updatesOverdue ? 'danger' : undefined} />
            <Tile label="No reply 72h+" value={d.kpis.waitingReview} sub="review and close" tone={d.kpis.waitingReview ? 'warning' : undefined} />
            {d.kpis.outcomeNeeded ? <Tile label="Closed without outcome" value={d.kpis.outcomeNeeded} sub="closed in Intercom; record how it ended" tone="warning" /> : null}
          </div>

          <Panel title="Next to breach" className="border-brand/30" action={<span className="text-tiny text-text-muted">{d.kpis.created} created · {d.kpis.resolved} resolved ({pct(d.kpis.resolutionRate)}) in period</span>}>
            {d.nextToBreach.length === 0 ? (
              <p className="text-sm text-text-muted">Nothing open with a running clock.</p>
            ) : (
              <ul className="grid gap-1.5">
                {d.nextToBreach.slice(0, 10).map((n) => {
                  const left = n.deadlineAt ? Date.parse(n.deadlineAt) - now : 0
                  return (
                    <li key={n.id}>
                      <button type="button" onClick={() => open(n.id)} className="grid w-full grid-cols-[6.5rem_1fr_auto] items-center gap-3 rounded-md px-2 py-1.5 text-left hover:bg-accent max-sm:grid-cols-1">
                        <SeverityBadge severity={n.severity} size="xs" />
                        <span className="min-w-0 truncate text-sm">{n.reference} · {n.title} <span className="text-text-muted">· {n.clientName ?? '—'} · {n.team}</span></span>
                        <span className="flex flex-col items-end text-right leading-tight max-sm:items-start max-sm:text-left" title={deadlineTitle(n.milestone, n.deadlineAt, now)}>
                          <span className={cn('num text-xs font-semibold', left < 0 ? 'text-danger' : n.status === 'at_risk' ? 'text-warning' : 'text-text-secondary')}>{lateness(left)}</span>
                          <span className="text-tiny text-text-muted">{MILESTONE_PENDING[n.milestone]}</span>
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </Panel>

          <div className="grid gap-4 xl:grid-cols-2">
            <Panel title="Teams">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr><Th>Team</Th><Th right>Open</Th><Th right>Unclaimed</Th><Th right>At risk</Th><Th right>Breached</Th><Th right>SLA met</Th><Th right>Oldest</Th></tr></thead>
                  <tbody>
                    {d.byTeam.map((t) => (
                      <tr key={t.team} className="border-t border-border-soft">
                        <Td><span className="inline-flex items-center gap-2"><span className={cn('size-2 rounded-full', FLAG_DOT[t.flag])} />{t.team}</span></Td>
                        <Td right>{t.open}</Td>
                        <Td right className={t.unclaimed ? 'text-warning' : ''}>{t.unclaimed}</Td>
                        <Td right className={t.atRisk ? 'text-warning' : ''}>{t.atRisk}</Td>
                        <Td right className={t.breached ? 'font-semibold text-danger' : ''}>{t.breached}</Td>
                        <Td right>{pct(t.slaMetPct)}</Td>
                        <Td right>{dur(t.oldestOpenMs)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-tiny text-text-muted">Red: a breach, or SLA met below 95%. Amber: something at risk or unclaimed.</p>
            </Panel>

            <Panel title="SLA by priority">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <Th>Priority</Th><Th right>Open</Th><Th right>Breached</Th>
                      {(['response', 'triage', 'pickup', 'restore', 'fix'] as Milestone[]).map((m) => <Th key={m} right>{MILESTONE_LABEL[m].replace('Engineering ', '')}</Th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {d.bySeverity.filter((s) => s.open || s.slaMetPct !== null).map((s) => (
                      <tr key={s.severity} className="border-t border-border-soft">
                        <Td>{s.severity === 'untriaged' ? <ToneBadge tone="accent" size="xs">Untriaged</ToneBadge> : <SeverityBadge severity={s.severity} size="xs" />}</Td>
                        <Td right>{s.open}</Td>
                        <Td right className={s.breached ? 'font-semibold text-danger' : ''}>{s.breached}</Td>
                        {(['response', 'triage', 'pickup', 'restore', 'fix'] as Milestone[]).map((m) => <Td key={m} right>{pct(s.met[m])}</Td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-tiny text-text-muted">Share of milestones met in the period, per priority.</p>
            </Panel>
          </div>

          <div className="grid gap-4 xl:grid-cols-3">
            <Panel title="Aging of open tickets"><Aging d={d} /></Panel>
            <Panel title="Opened vs closed"><Trend d={d} /></Panel>
            <Panel title="Top causes (fixed in period)">
              {d.causes.length === 0 ? (
                <p className="text-sm text-text-muted">No fixes recorded in this period.</p>
              ) : (
                <ul className="grid gap-1.5">
                  {d.causes.slice(0, 8).map((c) => (
                    <li key={c.cause} className="grid grid-cols-[1fr_auto] items-center gap-2 text-sm">
                      <span className="relative overflow-hidden rounded px-2 py-1">
                        <span className="absolute inset-y-0 left-0 bg-brand-softer" style={{ width: `${(c.count / Math.max(1, d.causes[0]!.count)) * 100}%` }} />
                        <span className="relative">{c.cause}</span>
                      </span>
                      <span className="num text-xs">{c.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <Panel title="Clients">
              <div className="max-h-96 overflow-auto">
                <table className="w-full">
                  <thead><tr><Th>Client</Th><Th>Stage</Th><Th right>Open</Th><Th right>New</Th><Th right>P1/P2</Th><Th right>Missed SLAs</Th></tr></thead>
                  <tbody>
                    {d.byClient.map((c) => (
                      <tr key={`${c.clientId}-${c.clientName}`} className="border-t border-border-soft">
                        <Td><span className="inline-flex items-center gap-2"><span className={cn('size-2 rounded-full', FLAG_DOT[c.flag])} />{c.clientName}</span></Td>
                        <Td className="text-xs text-text-secondary">{c.lifecycle === 'C1' ? 'Hypercare' : c.lifecycle === 'C2' ? 'Pilot' : c.lifecycle === 'C3' ? 'Live' : '—'}</Td>
                        <Td right>{c.open}</Td>
                        <Td right>{c.created}</Td>
                        <Td right>{c.s1s2}</Td>
                        <Td right className={c.breaches ? 'font-semibold text-danger' : ''}>{c.breaches}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
            <Panel title="Products">
              <table className="w-full">
                <thead><tr><Th>Product</Th><Th right>New in period</Th><Th right>Open</Th><Th right>SLA met</Th></tr></thead>
                <tbody>
                  {d.byProduct.map((p) => (
                    <tr key={p.product} className="border-t border-border-soft">
                      <Td>{p.product}</Td>
                      <Td right>{p.created}</Td>
                      <Td right>{p.open}</Td>
                      <Td right>{pct(p.slaMetPct)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </div>
          <p className="text-tiny text-text-muted">Updated {new Date(d.generatedAt).toLocaleTimeString()} · refreshes every minute.</p>
        </div>
      )}
      <InsightsSection onOpen={open} />
    </div>
  )
}
